import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Grid, Show, Text, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';
import {z} from 'zod';

import {useEffect, useRef} from 'react';

import {
  useCorrectVehicleMileageMutation,
  useGetVehicleMileageQuery,
  usePostVehicleMileageMutation,
  VehicleMileageAction,
} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {handleApiError} from '../../../utils/handleApiError';

export interface RecordVehicleMileageFormProps {
  vehicleId: string;
  recordId: string;
  milage: number;
  action: VehicleMileageAction;
  recordTitle: string;
  onCompleted?: () => void;
  onCancelled?: () => void;
}

const schema = z.object({
  note: z.string().nullable(),
  isCorrection: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

export function RecordVehicleMileageForm(props: RecordVehicleMileageFormProps) {
  const [handleRecordMilage] = usePostVehicleMileageMutation();
  const [correctRecordMilage] = useCorrectVehicleMileageMutation();
  const isResolutionHandledRef = useRef(false);

  const formatNumber = useFormatNumber();

  const {data: vehicleMilage, isLoading: isLoadingVehicleMilage} = useGetVehicleMileageQuery({
    vehicleId: props.vehicleId,
  });

  const defaultValues: FormValues = {
    isCorrection: props.milage < (vehicleMilage?.mileage ?? 0),
    note: null,
  };

  const originalMilage = vehicleMilage?.mileage ?? 0;
  const newMilage = props.milage;

  const originalMilageText = `${formatNumber(originalMilage)} ${i18n.t('general.metric.km')}`;
  const newMilageText = `${formatNumber(newMilage)} ${i18n.t('general.metric.km')}`;

  const handleSubmit: FormSubmitHandler<FormValues> = async (submitData) => {
    const submitAction = submitData.isCorrection ? correctRecordMilage : handleRecordMilage;

    await submitAction({
      vehicleId: props.vehicleId,
      correctMileageRequestBody: {mileage: newMilage, note: submitData.note},
      writeMileageRequestBody: {
        id: props.recordId,
        mileage: newMilage,
        action: submitData.isCorrection ? 'correction' : props.action,
        title: props.recordTitle,
        note: null,
      },
    })
      .unwrap()
      .then(() => {
        isResolutionHandledRef.current = true;

        if (props.onCompleted) {
          props.onCompleted();
        }

        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  useEffect(() => {
    return () => {
      if (!isResolutionHandledRef.current && props.onCancelled) {
        props.onCancelled();
      }
    };
  }, [props]);

  return (
    <DataStatus isLoading={isLoadingVehicleMilage}>
      <Form<FormValues>
        experimentalZodSchema={schema}
        onSubmit={handleSubmit}
        defaultValues={defaultValues}
      >
        {(control, formApi) => {
          const isCorrection = formApi.watch('isCorrection');

          return (
            <VStack spacing={4}>
              <FormField
                control={control}
                name="isCorrection"
                type="checkbox"
                label={i18n.t('entity.vehicle.labels.confirmIncorrectMilage')}
              />

              <Show when={isCorrection}>
                <Alert
                  type="inline"
                  variant="warning"
                  title={i18n.t('entity.vehicle.labels.milageCorrection')}
                  message={i18n.t('entity.vehicle.labels.milageCorrectionDescription')}
                />
              </Show>

              <Card variant="inlineGrey">
                <Grid columns={2}>
                  <VStack spacing={1}>
                    <Text size="small" color="secondary">
                      {i18n.t('entity.vehicle.labels.previousMilage')}
                    </Text>

                    <Text size="large" color="tertiary" alternative strikethrough={isCorrection}>
                      {originalMilageText}
                    </Text>
                  </VStack>

                  <VStack spacing={1}>
                    <Text size="small" color="secondary">
                      {i18n.t('entity.vehicle.labels.newCorrectMilage')}
                    </Text>

                    <Text size="large" alternative color={isCorrection ? 'warning' : 'success'}>
                      {newMilageText}
                    </Text>
                  </VStack>
                </Grid>
              </Card>

              <Show when={isCorrection}>
                <FormField
                  control={control}
                  name="note"
                  type="textarea"
                  minRows={3}
                  label={i18n.t('entity.vehicle.labels.correctionReasonOptional')}
                  placeholder={i18n.t('entity.vehicle.labels.correctionReasonPlaceholder')}
                />
              </Show>

              <ButtonGroup align="right">
                <Button
                  variant="secondary"
                  title={i18n.t('general.labels.dismiss')}
                  onClick={() => {
                    isResolutionHandledRef.current = true;

                    if (props.onCancelled) {
                      props.onCancelled();
                    }

                    closeCurrentDialog();
                  }}
                />
                <FormButton
                  control={control}
                  type="submit"
                  variant="primary"
                  title={i18n.t('entity.vehicle.actions.recordMilage')}
                />
              </ButtonGroup>
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}
